import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";
import { useDispatch, useSelector, } from "react-redux";
import {
  postHolidayThunk,
  updateHolidayThunk,
  addCalendarItem,
  // createEventThunk,
  updateEventThunk,
  createCompetitionThunk,
  updateCompetitionThunk,
  createPtmThunk,
  updatePtmThunk,
  createExamThunk,
  updateExamThunk,
  createCelebrationThunk,
  createAwarenessThunk,
  updateAwarenessThunk,
  fetchHolidayCategoriesThunk,
  fetchCompetitionCategoriesThunk,
  fetchExamCategoriesThunk,
  updateCelebrationThunk,
} from "../redux/academicCalendarSlice";
import userStore from "../../../zustand/userStore";
import { setStepIndex, setStepValid } from "../../../redux/globalJoyrideSlice";

const AddCalendarEventForm = ({ onClose, itemType, editItem }) => {
  const userData = userStore((state) => state.user);
  const dispatch = useDispatch();
  console.log(userData);
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [description, setDescription] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(""); // State for selected category

  const holidayCategories = useSelector(
    (state) => state.academicCalendar.holidayCategories
  );
  const competitionCategories = useSelector(
    (state) => state.academicCalendar.competitionCategories
  );
  const examCategories = useSelector(
    (state) => state.academicCalendar.examCategories
  );

  // Memoize categories based on itemType
  const categories = useMemo(() => {
    if (itemType === "holiday") {
      return holidayCategories;
    } else if (itemType === "competition") {
      return competitionCategories;
    } else if (itemType === "exam") {
      return examCategories;
    }
    return [];
  }, [itemType, holidayCategories, competitionCategories, examCategories]);

  const dateRangeItemTypes = ["holiday", "event", "competition", "exam", "ptm", "celebration", "awareness"];

  // Effect to fetch categories when itemType changes
  useEffect(() => {
    if (itemType === "holiday") {
      dispatch(fetchHolidayCategoriesThunk());
    } else if (itemType === "competition") {
      dispatch(fetchCompetitionCategoriesThunk());
    } else if (itemType === "exam") {
      dispatch(fetchExamCategoriesThunk());
    }
  }, [dispatch, itemType]);

  // Effect to set initial form values when editItem or itemType changes
  useEffect(() => {
    // Reset all form fields when itemType or editItem changes (e.g., switching form type or starting new add)
    setName("");
    setStartDate("");
    setEndDate("");
    setDescription("");
    setStartTime("");
    setEndTime("");
    // Do NOT reset selectedCategory here, it's handled by its own useEffect

    if (editItem) {
      setName(editItem.name || editItem.details || "");
      if (dateRangeItemTypes.includes(itemType)) {
        setStartDate(
          editItem.start_date ? moment(editItem.start_date).format("YYYY-MM-DD") : ""
        );
        setEndDate(
          editItem.end_date ? moment(editItem.end_date).format("YYYY-MM-DD") : ""
        );
      } else {
        setStartDate(editItem.date ? moment(editItem.date).format("YYYY-MM-DD") : "");
      }

      setDescription(editItem.description || editItem.details || "");
      setStartTime(editItem.start_time || "");
      setEndTime(editItem.end_time || "");
    }
  }, [editItem, itemType]); // Removed dateRangeItemTypes as it's a constant and doesn't need to trigger re-renders

  // New useEffect to handle setting selected category once categories are loaded
  // Replace the existing useEffect with this one
  useEffect(() => {
    if (editItem && categories.length > 0) {
      if (itemType === "holiday" && editItem.category_id) {
        setSelectedCategory(String(editItem.category_id));
      } else if (itemType === "competition") {
        // Use both possible property names
        const competitionCatId = editItem.competition_id || editItem.competition_cat_id;
        if (competitionCatId) {
          const categoryExists = categories.some(
            cat => String(cat.id) === String(competitionCatId)
          );
          if (categoryExists) {
            setSelectedCategory(String(competitionCatId));
          } else {
            setSelectedCategory("");
            console.warn(`Competition category ID ${competitionCatId} not found`);
          }
        }
      } else if (itemType === "exam") {
        const examCatId = editItem.category_id || editItem.exam_cat_id; // ✅ handle both keys
        if (examCatId) {
          const categoryExists = categories.some(
            cat => String(cat.id) === String(examCatId)
          );
          if (categoryExists) {
            setSelectedCategory(String(examCatId));
          } else {
            setSelectedCategory("");
            console.warn(`Exam category ID ${examCatId} not found in fetched categories.`);
          }
        }
      }

    } else if (!editItem) {
      setSelectedCategory("");
    }
  }, [editItem, itemType, categories]); // Depend on categories to re-evaluate when they load


  const getTitle = () => {
    switch (itemType) {
      case "event":
      case "celebration":
        return editItem ? "Edit Event / Celebration" : "Add Event / Celebration";
      case "awareness":
        return editItem ? "Edit Awareness Day" : "Add Awareness Day";
      case "holiday":
        return editItem ? "Edit Holiday" : "Add Holiday";
      case "competition":
        return editItem ? "Edit Competition" : "Add Competition";
      case "exam":
        return editItem ? "Edit Examination / Test" : "Add Examination / Test";
      case "ptm":
        return editItem ? "Edit Parent-Teacher Meeting" : "Add Parent-Teacher Meeting";
      default:
        return "Add Calendar Item";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || (dateRangeItemTypes.includes(itemType) && (!startDate || !endDate))) {
      alert("Please fill in required fields (Name, Start Date, End Date).");
      return;
    }

    if (dateRangeItemTypes.includes(itemType) && moment(startDate).isAfter(moment(endDate))) {
      alert("Start date cannot be after end date.");
      return;
    }
    console.log(userData.id, "user");
    try {
      let payload = {
        school_id: userData?.school_id || 1,
        user_id: userData.id,
        details: name,
        status: "active",
      };

      if (dateRangeItemTypes.includes(itemType)) {
        payload = {
          ...payload,
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate).format("YYYY-MM-DD"),
        };
        if (itemType === "holiday") {
          payload.category_id = Number(selectedCategory);
        } else if (itemType === "competition") {
          if (selectedCategory) {
            payload.competition_cat_id = Number(selectedCategory);
          }
        } else if (itemType === "exam") {
          if (!selectedCategory) {
            alert("Please select a category for the exam.");
            return;
          }
          payload.exam_cat_id = Number(selectedCategory);
        }

      }
      if (description) {
        payload.description = description;
      }

      if (!editItem && itemType === "exam") {
        // 1️⃣ First API call
        const response = await dispatch(createExamThunk(payload)).unwrap();

        // 2️⃣ Holiday confirmation
        if (response?.requireConfirmation) {
          const confirmChoice = window.confirm(
            response.message || "Holidays exist in the selected dates. Do you still want to proceed?"
          );

          if (!confirmChoice) return;

          const confirmedPayload = { ...payload, isConfirm: true };

          // 3️⃣ Second API call
          await dispatch(createExamThunk(confirmedPayload)).unwrap();
        }

        alert("Exam saved!");
        onClose();
        requestAnimationFrame(() => {
          dispatch(setStepIndex(14));
        });
        return; // 🔥 IMPORTANT: STOP HERE → do NOT run the generic dispatch
      }


      let actionToDispatch;
      if (editItem) {
        switch (itemType) {
          case "holiday":
            actionToDispatch = updateHolidayThunk({ id: editItem.id, holidayData: payload });
            break;
          case "event":
            actionToDispatch = updateEventThunk({ id: editItem.id, eventData: payload });
            break;
          case "competition":
            actionToDispatch = updateCompetitionThunk({ id: editItem.id, competitionData: payload });
            break;
          case "ptm":
            actionToDispatch = updatePtmThunk({ id: editItem.id, ptmData: payload });
            break;
          case "exam":
            actionToDispatch = updateExamThunk({ id: editItem.id, examData: payload });
            break;
          case "celebration":
            actionToDispatch = updateCelebrationThunk({ id: editItem.id, celebrationData: payload });
            break;
          case "awareness":
            actionToDispatch = updateAwarenessThunk({ id: editItem.id, awarenessData: payload });
            break;
          default:
            actionToDispatch = addCalendarItem({ ...editItem, ...payload });
            break;
        }
      } else {
        switch (itemType) {
          case "holiday":
            actionToDispatch = postHolidayThunk(payload);
            break;
          // case "event":
          //   actionToDispatch = createEventThunk(payload);
          //   break;
          case "competition":
            actionToDispatch = createCompetitionThunk(payload);
            break;
          case "celebration":
            actionToDispatch = createCelebrationThunk(payload);
            break;
          case "awareness":
            actionToDispatch = createAwarenessThunk(payload);
            break;
          case "ptm":
            actionToDispatch = createPtmThunk(payload);
            break;
          //             case "exam": {
          //   // 1️⃣ FIRST API CALL
          //   const response = await dispatch(createExamThunk(payload)).unwrap();

          //   // 2️⃣ CHECK FOR HOLIDAY WARNING
          //   if (response?.requireConfirmation) {
          //     const userConfirmed = window.confirm(
          //       "Holidays exist in the selected dates. Do you still want to proceed?"
          //     );

          //     if (!userConfirmed) return; // STOP if cancelled

          //     // 3️⃣ SECOND CALL WITH CONFIRM FLAG
          //     const confirmedPayload = { ...payload, isConfirm: true };

          //     await dispatch(createExamThunk(confirmedPayload)).unwrap();
          //   }

          //   // Exit switch early → do NOT go to the default dispatch
          //   break;
          // }

          default:
            actionToDispatch = addCalendarItem(payload);
            break;
        }
      }

      await dispatch(actionToDispatch).unwrap();

      alert(`${getTitle().replace("Add ", "").replace("Edit ", "")} saved!`);
      onClose();
      // 2️⃣ AFTER list is rendered → move Joyride
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const listStepByType = {
            celebration: 10,
            awareness: 10,
            holiday: 11,
            competition: 11,
            ptm:5
          };

          const nextStep = listStepByType[itemType];

          if (nextStep !== undefined) {
            dispatch(setStepIndex(nextStep));
          }
        });
      });
    } catch (error) {
      console.error("Failed to save calendar item:", error);
      alert(`Error saving ${getTitle().replace("Add ", "").replace("Edit ", "")}. Please try again. ${error.response?.data?.message || error.message}`);
    }
  };

  const containerStyle = {
    maxWidth: "900px",
    margin: "15px auto",
    padding: "15px 30px",
    backgroundColor: "rgb(123 47 247 / 51%)",
    // backgroundColor: "#ffffff",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    borderRadius: "12px",
    fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
  };
  return (
    <div className="card p-3 shadow-sm" style={containerStyle}>
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="name" className="form-label">Name:</label>
          <input
            type="text"
            id="name"
            className="form-control"
            data-tour={`${itemType}-name`}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              dispatch(setStepValid(true))
            }}
            required
          />
        </div>

        {(itemType === "holiday" || itemType === "competition" || itemType === "exam") && (
          <div className="mb-3">
            <label htmlFor="category" className="form-label">  {itemType === "competition" ? "Competition Type" : "Category"}:</label>
            <select
              id="category"
              className="form-select"
              data-tour={`${itemType}-category`}
              value={selectedCategory}
              onChange={(e) => {
                console.log("Selected category changed:", e.target.value); setSelectedCategory(e.target.value);
                dispatch(setStepValid(true))
              }}
              required
            >
              <option value="">Select {itemType === "competition" ? "Competition Type" : "Category"}</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {dateRangeItemTypes.includes(itemType) && (
          <>
            <div className="mb-3">
              <label htmlFor="startDate" className="form-label">Start Date:</label>
              <input
                type="date"
                id="startDate"
                className="form-control"
                data-tour={`${itemType}-start-date`}
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  dispatch(setStepValid(true))
                }}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="endDate" className="form-label">End Date:</label>
              <input
                type="date"
                id="endDate"
                className="form-control"
                data-tour={`${itemType}-end-date`}
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  dispatch(setStepValid(true))
                }}
                required
              />
            </div>
          </>
        )}

        {/* {(itemType === "event" || itemType === "ptm" || itemType === "exam" || itemType === "celebration" || itemType === "awareness") && (
          <>
            <div className="mb-3">
              <label htmlFor="startTime" className="form-label">Start Time (Optional):</label>
              <input
                type="time"
                id="startTime"
                className="form-control"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="endTime" className="form-label">End Time (Optional):</label>
              <input
                type="time"
                id="endTime"
                className="form-control"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </>
        )} */}

        <div className="mb-3">
          <label htmlFor="description" className="form-label">Description (Optional):</label>
          <textarea
            id="description"
            className="form-control"
            rows="3"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          ></textarea>
        </div>

        <div className="d-flex gap-2">
          <button type="submit" className="btn btn-primary" data-tour={`${itemType}-save`}>
            {editItem ? "Update" : "Save"} {getTitle().replace("Add ", "").replace("Edit ", "")}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCalendarEventForm;
